import type { GlobalOptions } from '../cli/args.js';
import { CliError, ExitCode } from '../cli/errors.js';
import { type DiscoveredDevice, MdnsDiscovery } from '../discovery/mdns.js';
import { type DeviceConfig, loadCliConfig, parseDevice } from './config.js';

export async function scanDevices(timeoutMs: number): Promise<DiscoveredDevice[]> {
  const discovery = new MdnsDiscovery();
  const found = new Map<string, DiscoveredDevice>();
  discovery.on('discovered', (device) => found.set(device.id, device));
  try {
    discovery.start();
    await new Promise((resolve) => setTimeout(resolve, timeoutMs));
    return [...found.values()];
  } finally {
    discovery.stop();
  }
}

export async function resolveDevice(options: GlobalOptions): Promise<DeviceConfig> {
  const config = loadCliConfig(options.configPath);
  if (options.device) {
    const parsed = parseDevice(options.device);
    const configured = config.devices.find(
      (device) =>
        device.id === parsed.id || (device.host === parsed.host && device.port === parsed.port),
    );
    if (!configured) return parsed;
    const schemeExplicit = /^[a-z]+:\/\//i.test(options.device);
    const authorityAndPath = schemeExplicit
      ? options.device.slice(options.device.indexOf('://') + 3)
      : options.device;
    const pathExplicit = authorityAndPath.includes('/');
    return {
      ...configured,
      ...parsed,
      apiKey: configured.apiKey,
      scheme: schemeExplicit ? parsed.scheme : configured.scheme,
      apiPath: pathExplicit ? parsed.apiPath : configured.apiPath,
    };
  }
  if (config.defaultDevice) {
    const configured = config.devices.find(
      (device) =>
        device.id === config.defaultDevice ||
        device.aliases?.includes(config.defaultDevice as string),
    );
    return configured ?? parseDevice(config.defaultDevice);
  }
  if (config.devices.length === 1) return config.devices[0];
  if (config.devices.length > 1) {
    throw new CliError(
      `Multiple devices configured; pass --device. Available: ${config.devices.map((d) => d.id).join(', ')}`,
      ExitCode.NoDevice,
    );
  }

  const discovered = await scanDevices(
    Math.min(5000, Math.max(1000, options.timeoutSeconds * 1000)),
  );
  if (discovered.length === 1) {
    const device = discovered[0];
    return { id: device.id, host: device.host, port: device.port };
  }
  if (discovered.length > 1) {
    throw new CliError(
      `Multiple devices discovered; pass --device. Available: ${discovered.map((d) => d.id).join(', ')}`,
      ExitCode.NoDevice,
    );
  }
  throw new CliError(
    'No Zaparoo device configured or discovered. Pass --device <host:port> or set ZAPAROO_DEVICES.',
    ExitCode.NoDevice,
  );
}
